import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    ControlStepDownMeasuresKeys,
    ControlMeasuresConstantType,
    ControlStyleConstantType,
    HitAreaConstantType,
    ControlMeasuresKeys,
    ControlSizeType,
    HitAreaMeasures,
    ControlMeasures,
    ControlStyles
} from "../../types/theme";

const CONTAINER_STYLE_KEYS: Array<keyof ViewStyle> = [
    "marginHorizontal",
    "marginVertical",
    "marginBottom",
    "marginStart",
    "marginRight",
    "flexShrink",
    "marginLeft",
    "alignSelf",
    "flexBasis",
    "marginEnd",
    "marginTop",
    "position",
    "flexGrow",
    "display",
    "margin",
    "zIndex",
    "bottom",
    "inset",
    "start",
    "right",
    "flex",
    "left",
    "end",
    "top"
];

const PERCENT_SIZE_KEYS: Array<"height" | "width"> = [
    "height",
    "width"
];

const PERCENT_PATTERN = /%$/;

export const CONTROL_SIZES: Record<ControlSizeType, ControlMeasuresKeys> = {
    medium: {
        labelVariant: "labelMediumSize",
        valueVariant: "bodyMediumSize",
        paddingHorizontal: "medium",
        iconSize: "medium",
        height: "medium"
    },
    large: {
        labelVariant: "labelLargeSize",
        valueVariant: "bodyLargeSize",
        paddingHorizontal: "large",
        iconSize: "large",
        height: "large"
    },
    small: {
        labelVariant: "labelSmallSize",
        valueVariant: "bodySmallSize",
        paddingHorizontal: "small",
        iconSize: "small",
        height: "small"
    }
};

export const CONTROL_STEP_DOWN_SIZES: Record<ControlSizeType, ControlStepDownMeasuresKeys> = {
    large: {
        labelVariant: "labelMediumSize",
        valueVariant: "bodyMediumSize",
        isIconOffset: false,
        iconSize: "small",
        height: "large"
    },
    medium: {
        labelVariant: "labelMediumSize",
        valueVariant: "bodyMediumSize",
        isIconOffset: false,
        iconSize: "small",
        height: "medium"
    },
    small: {
        labelVariant: "labelSmallSize",
        valueVariant: "bodySmallSize",
        isIconOffset: true,
        iconSize: "small",
        height: "small"
    }
};

export const getControlMeasures = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    iconSizes,
    step = 0,
    size
}: ControlMeasuresConstantType): ControlMeasures => {
    if(step === -1) {
        const stepDownSize = CONTROL_STEP_DOWN_SIZES[size];

        const minHeight = controlHeights[stepDownSize.height] - shapeConfigs.chipHeightOffset;

        const iconSize = iconSizes[stepDownSize.iconSize];

        return {
            iconSize: stepDownSize.isIconOffset ? iconSize - shapeConfigs.chipIconOffset : iconSize,
            paddingHorizontal: minHeight / 2 - shapeConfigs.chipPaddingOffset,
            labelVariant: stepDownSize.labelVariant,
            valueVariant: stepDownSize.valueVariant,
            minHeight
        };
    }

    const currentSize = CONTROL_SIZES[size];

    return {
        paddingHorizontal: controlPaddings[currentSize.paddingHorizontal],
        minHeight: controlHeights[currentSize.height],
        iconSize: iconSizes[currentSize.iconSize],
        labelVariant: currentSize.labelVariant,
        valueVariant: currentSize.valueVariant
    };
};

export const getHitArea = ({
    touchTargets,
    pointerType,
    visualSize
}: HitAreaConstantType): HitAreaMeasures => {
    const touchTarget = pointerType === "coarse" ? touchTargets.coarse : touchTargets.fine;

    const hitSize = Math.max(visualSize, touchTarget);

    return {
        minHeight: hitSize,
        minWidth: hitSize
    };
};

export const splitControlStyle = ({
    style
}: ControlStyleConstantType): ControlStyles => {
    const flatStyle: ViewStyle = StyleSheet.flatten(style) ?? {};

    const containerStyle: ViewStyle = {};

    const surfaceStyle: ViewStyle = {};

    (Object.keys(flatStyle) as Array<keyof ViewStyle>).forEach((key) => {
        Object.assign(CONTAINER_STYLE_KEYS.includes(key) ? containerStyle : surfaceStyle, {
            [key]: flatStyle[key]
        });
    });

    PERCENT_SIZE_KEYS.forEach((key) => {
        const size = flatStyle[key];

        if(typeof size !== "string" || !PERCENT_PATTERN.test(size)) {
            return;
        }

        Object.assign(containerStyle, {
            [key]: size
        });

        Object.assign(surfaceStyle, key === "width" ? stylesheet.fullWidth : stylesheet.fullHeight);
    });

    if((flatStyle.flex ?? 0) > 0 || (flatStyle.flexGrow ?? 0) > 0 || flatStyle.alignSelf === "stretch") {
        Object.assign(surfaceStyle, stylesheet.stretchedSurface);
    }

    return {
        containerStyle,
        surfaceStyle
    };
};

export const getRowIconGap = ({
    controlHeights,
    rowHeight,
    spaces
}: {
    controlHeights: NCoreUIKit.ControlHeights;
    spaces: NCoreUIKit.Spaces;
    rowHeight: number;
}): number => {
    if(rowHeight <= controlHeights.small) {
        return spaces.spacingSm;
    }

    if(rowHeight <= controlHeights.medium) {
        return spaces.spacingSm + spaces.spacingXs;
    }

    return spaces.spacingMd;
};

const stylesheet = StyleSheet.create({
    stretchedSurface: {
        alignSelf: "stretch",
        flexGrow: 1
    },
    fullHeight: {
        height: "100%"
    },
    fullWidth: {
        width: "100%"
    }
});
